// Template for /webcast/<slug> pages.
// Converted from https://www.dbos.dev/webcast/are-your-agents-on-acid.

import type { Webcast } from '@content/types';
import { SiteLayout } from '@components/layout/SiteLayout';
import { FooterCtaBuildYourReliableBackend } from '@components/layout/FooterCtas';
import { Img } from '@components/cms/Img';
import { SlideIn } from '@components/interactions';
import { ShareLink } from '@components/webflow';
import { cx } from '@components/webflow/element';
import { longDate } from '@lib/format';

export function WebcastPage({ webcast, body }: { webcast: Webcast; body: string }) {
  return (
    <SiteLayout banner={true} footerCta={<FooterCtaBuildYourReliableBackend />}>
      <section className="section_webcast">
        <SlideIn className="padding-global" as="div">
          <div className="container-large">
            <div className="padding-section-large padding-top">
              <div className="max-width-xlarge margin-auto">
                <div className="margin-bottom margin-small">
                  <h1 className="heading-style-h2">{webcast.title}</h1>
                </div>
                <div className="margin-bottom margin-medium">
                  <div className="post_meta-wrapper">
                    <div className="post_author-wrapper">
                      <div className="blog-header_author-image-wrapper">
                        <Img image={webcast.speaker.image} loading="lazy" className="blog-header_author-image" />
                      </div>
                      <div className="text-size-small">{webcast.speaker.name}</div>
                    </div>
                    <div className="post_date-wrapper">
                      <div className="w-embed">
                        <span className="text-size-small">{`Recorded on ${longDate(webcast.recorded)}`}</span>
                      </div>
                      <div className="tagline-container text-size-small">{webcast.tagline}</div>
                    </div>
                  </div>
                </div>
                <div className="margin-bottom margin-medium">
                  <div className="webcast-embed w-embed w-iframe">
                    <iframe
                      width="100%"
                      height="100%"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      src={webcast.video.src}
                      title={webcast.video.title}
                    >
                      {'\n'}
                    </iframe>
                  </div>
                </div>
                <div className="margin-bottom margin-medium">
                  <div
                    className={cx('text-body-rich w-richtext', !body && 'w-dyn-bind-empty')}
                    dangerouslySetInnerHTML={{ __html: body }}
                  />
                </div>
                <div className="post_meta-wrapper">
                  {webcast.slides && (
                    <div data-wf--button--variant="secondary-small" className="button-wrapper">
                      <a
                        className="button w-variant-4864a870-d315-9569-ac8b-4ceaecb0466d w-inline-block"
                        href={webcast.slides.href}
                      >
                        <div className="text-block-4">{webcast.slides.label}</div>
                      </a>
                    </div>
                  )}
                  <div className="w-layout-hflex share-post-wrapper">
                    <div className="share-post-copy">Share this webcast</div>
                    <ShareLink
                      fb-social-type="linkedin"
                      fb-social="true"
                      href="#"
                      className="social-link-wrapper w-inline-block"
                    >
                      <div className="social-link">
                        <div className="social-link-code w-embed">
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 12 12"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            {'\n'}
                            <path
                              d="M0 1.61369C0 1.22889 0.135139 0.911425 0.405405 0.661313C0.675672 0.411189 1.02703 0.286133 1.45946 0.286133C1.88417 0.286133 2.2278 0.409261 2.49035 0.655541C2.76061 0.909509 2.89575 1.24043 2.89575 1.64833C2.89575 2.01773 2.76448 2.32557 2.50193 2.57185C2.23166 2.82581 1.87645 2.9528 1.43629 2.9528H1.42471C0.999996 2.9528 0.656375 2.82581 0.393822 2.57185C0.13127 2.31788 0 1.99849 0 1.61369ZM0.150579 11.7147V4.0033H2.72201V11.7147H0.150579ZM4.14672 11.7147H6.71815V7.40878C6.71815 7.13942 6.74904 6.93162 6.81081 6.78541C6.91891 6.52374 7.08301 6.30248 7.30309 6.12163C7.52317 5.94077 7.79922 5.85034 8.13127 5.85034C8.99614 5.85034 9.42857 6.43139 9.42857 7.59349V11.7147H12V7.29334C12 6.15433 11.7297 5.29046 11.1892 4.70172C10.6486 4.11297 9.93437 3.8186 9.04633 3.8186C8.05019 3.8186 7.27413 4.24573 6.71815 5.09998V5.12307H6.70656L6.71815 5.09998V4.0033H4.14672C4.16216 4.24957 4.16988 5.01532 4.16988 6.30056C4.16988 7.58579 4.16216 9.3905 4.14672 11.7147Z"
                              fill="currentColor"
                            />
                            {'\n'}
                          </svg>
                        </div>
                      </div>
                    </ShareLink>
                    <ShareLink
                      fb-social-type="twitter"
                      fb-social="true"
                      href="#"
                      className="social-link-wrapper w-inline-block"
                    >
                      <div className="social-link">
                        <div className="social-link-code w-embed">
                          <svg
                            width="11"
                            height="11"
                            viewBox="0 0 11 11"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            {'\n'}
                            <g clipPath="url(#clip0_110_4018)">
                              {'\n'}
                              <path
                                d="M6.54209 4.65827L10.6343 0H9.66495L6.11015 4.04388L3.27306 0H0L4.29118 6.11565L0 11H0.969389L4.72092 6.72857L7.71774 11H10.9908M1.31926 0.715868H2.80852L9.66421 10.3193H8.17459"
                                fill="currentColor"
                              />
                              {'\n'}
                            </g>
                            {'\n'}
                            <defs>
                              {'\n'}
                              <clipPath id="clip0_110_4018">
                                {'\n'}
                                <rect width="10.9908" height="11" fill="white" />
                                {'\n'}
                              </clipPath>
                              {'\n'}
                            </defs>
                            {'\n'}
                          </svg>
                        </div>
                      </div>
                    </ShareLink>
                    <ShareLink
                      fb-social-type="facebook"
                      fb-social="true"
                      href="#"
                      className="social-link-wrapper w-inline-block"
                    >
                      <div className="social-link">
                        <div className="social-link-code w-embed">
                          <svg width="8" height="12" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                            {'\n'}
                            <path
                              d="M4.9752 12V6.52624H6.905L7.19392 4.393H4.97515V3.03102C4.97515 2.4134 5.15527 1.99252 6.08556 1.99252L7.27202 1.99199V0.0840478C7.06681 0.0580973 6.36247 0 5.54315 0C3.83247 0 2.66131 0.994117 2.66131 2.81982V4.393H0.726562V6.52624H2.66131V11.9999H4.9752V12Z"
                              fill="currentColor"
                            />
                            {'\n'}
                          </svg>
                        </div>
                      </div>
                    </ShareLink>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </SlideIn>
      </section>
    </SiteLayout>
  );
}
